/**
 * GA4 GTag Wrapper
 * Abstração segura sobre window.gtag com queue para eventos
 * disparados antes do script carregar.
 */

import { config } from '../../config';
import { debug } from '../debug';
import { sanitize } from '../sanitize';
import { enqueueEvent } from '../serverProxy';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

interface QueuedEvent {
  eventName: string;
  params: Record<string, unknown>;
}

let eventQueue: QueuedEvent[] = [];
let isFlushing = false;

/**
 * Dispara um evento GA4.
 *
 * Modo server-side (produção): envia via /api/collect.
 * Modo client-side (dev): envia via gtag.js tradicional.
 */
export function gtagEvent(
  eventName: string,
  params: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return;

  const cleanParams = sanitize(params);

  // Server-side: envia para /api/collect (com batching automático)
  if (config.serverSide) {
    enqueueEvent(eventName, cleanParams);
    debug.event(eventName, cleanParams, { mode: 'server' });
    return;
  }

  // Client-side tradicional
  if (!window.gtag) {
    debug.warn('gtag not loaded, event queued:', eventName);
    eventQueue.push({ eventName, params: cleanParams });
    return;
  }

  sendClientSide(eventName, cleanParams);
}

function sendClientSide(eventName: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.gtag) return;

  const payload = {
    ...params,
    send_to: config.ga4Id,
    event_timestamp: new Date().toISOString(),
    debug_mode: config.isDev,
  };

  window.gtag('event', eventName, payload);
  debug.event(eventName, payload);
}

/**
 * Configura o gtag (gtag('config', ...)).
 * Deve ser chamado após o script GA4 carregar.
 */
export function gtagConfig(pagePath?: string, pageTitle?: string): void {
  if (typeof window === 'undefined' || !window.gtag) return;

  const configParams: Record<string, unknown> = {
    send_page_view: false, // controlamos manualmente via SPA
    cookie_flags: 'SameSite=None;Secure',
    debug_mode: config.isDev,
  };

  if (pagePath) configParams.page_path = pagePath;
  if (pageTitle) configParams.page_title = pageTitle;

  window.gtag('config', config.ga4Id, configParams);
  debug.log('gtag config sent', configParams);
}

/**
 * Envia todos os eventos enfileirados.
 * Chamado automaticamente quando o script GA4 carrega.
 */
export function flushQueuedEvents(): void {
  if (isFlushing || eventQueue.length === 0) return;
  isFlushing = true;

  debug.log('Flushing', eventQueue.length, 'queued events');

  for (const { eventName, params } of eventQueue) {
    gtagEvent(eventName, params);
  }

  eventQueue = [];
  isFlushing = false;
}

/**
 * Inicializa o dataLayer e a função gtag se ainda não existirem.
 * Chamado no bootstrap da aplicação.
 */
export function initDataLayer(): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function (...args: unknown[]) {
    window.dataLayer!.push(args);
  };

  window.gtag('js', new Date());
}
